import React, {useEffect, useState} from "react";
import {getJSON} from "../api.js";
import {Badge} from "./Badge.jsx";
import {RevisionHistory} from "./RevisionHistory.jsx";
import {ErrorState, LoadingState} from "./LoadingState.jsx";
import {PnlCharts} from "./PnlCharts.jsx";
import {TradesTable} from "./TradesTable.jsx";
import {formatDateTime, metricClass, money, signed} from "../format.js";

function Stat({label, value, className = ""}) {
    return (
        <div className="stat">
            <div className="metric-label">{label}</div>
            <div className={`metric ${className}`}>{value}</div>
        </div>
    );
}

function PlacementTable({strategy}) {
    if (!strategy.placements?.length) return <div className="empty">No placements are currently reported.</div>;
    return (
        <div className="table-wrap">
            <table>
                <thead>
                    <tr>
                        <th>Portfolio / account</th>
                        <th>Runtime</th>
                        <th className="num">Current risk</th>
                        <th>Lifecycle</th>
                        <th>Identity</th>
                    </tr>
                </thead>
                <tbody>
                    {strategy.placements.map((placement) => {
                        return (
                            <tr key={placement.id}>
                                <td>
                                    <a href={`#portfolio/${encodeURIComponent(placement.portfolioId)}`}>{placement.portfolioName}</a>
                                    <div className="subtle">{placement.account}</div>
                                </td>
                                <td>
                                    <div>{placement.runtime}</div>
                                    <div className="subtle">{placement.host}</div>
                                </td>
                                <td className="num">{money(placement.currentRiskUsd)}</td>
                                <td>
                                    <Badge value={placement.state}/>
                                    {" "}
                                    <Badge value={placement.enabled ? "enabled" : "disabled"}/>
                                </td>
                                <td>
                                    {placement.identityParity ? (
                                        <>
                                            <Badge value={placement.identityParity.status}/>
                                            {placement.identityParity.differences?.length ? (
                                                <div className="subtle">
                                                    {placement.identityParity.differences.length}
                                                    {" "}
                                                    field differences
                                                </div>
                                            ) : null}
                                        </>
                                    ) : <Badge value="UNAVAILABLE"/>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function Identity({strategy}) {
    return (
        <section className="detail-drawer-section">
            <h3>Running identity verification</h3>
            <div className="subtle">Implementation identity is evaluated independently from P&amp;L, risk, and lifecycle state.</div>
            <dl className="info-list">
                <div className="info-item">
                    <dt>Overall</dt>
                    <dd><Badge value={strategy.identityStatus || "UNAVAILABLE"}/></dd>
                </div>
                {strategy.placements.map((placement) => {
                    const parity = placement.identityParity;
                    return (
                        <div className="info-item" key={placement.id}>
                            <dt>{placement.account}</dt>
                            <dd>
                                <Badge value={parity?.status || "UNAVAILABLE"}/>
                                {parity?.differences?.length ? <div className="subtle">{parity.differences.map((difference) => difference.field).join(", ")}</div> : null}
                                {parity?.liveHeartbeatAt ? (
                                    <div className="subtle">
                                        heartbeat
                                        {" "}{formatDateTime(parity.liveHeartbeatAt)}
                                    </div>
                                ) : null}
                            </dd>
                        </div>
                    );
                })}
            </dl>
        </section>
    );
}

function RiskHistory({strategy}) {
    const entries = strategy.placements
        .flatMap((placement) => (placement.riskHistory || []).slice(-5)
            .map((point) => ({...point, portfolioName: placement.portfolioName, account: placement.account})))
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, 12);
    return entries.length ? (
        <div className="timeline">
            {entries.map((point, index) => (
                <div className="timeline-item" key={`${point.date}-${point.portfolioName}-${index}`}>
                    <div className="subtle mono">{point.date}</div>
                    <div>
                        <strong>{money(point.riskUsd)}</strong>
                        {" "}
                        ·
                        {" "}
                        {point.portfolioName}
                        {" "}
                        /
                        {" "}
                        {point.account}
                        <div className="subtle">
                            {point.source}
                            {" "}
                            · revision
                            {" "}
                            {point.revision}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    ) : <div className="empty">No risk history linked.</div>;
}

function Evidence({strategy}) {
    const evidence = [strategy.canonical, ...(strategy.evidence || [])].filter(Boolean);
    return evidence.length ? (
        <div className="timeline">
            {evidence.map((item, index) => (
                <div className="timeline-item" key={`${item.label}-${index}`}>
                    <div>
                        <Badge value={item.status}/>
                        <div className="subtle">{item.kind}</div>
                    </div>
                    <div>
                        <strong>{item.label}</strong>
                        <div className="subtle">
                            {item.runId || item.researchRef || ""}
                            {item.codeCommit ? ` · code ${item.codeCommit}` : ""}
                            {item.configurationRevision ? ` · config ${item.configurationRevision}` : ""}
                            {item.dataRevision ? ` · data ${item.dataRevision}` : ""}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    ) : <div className="empty">No evidence linked.</div>;
}

function Facts({strategy}) {
    const facts = [];
    Object.entries(strategy.facts || {}).forEach(([field, fact]) => facts.push({field, fact, placement: "strategy"}));
    strategy.placements.forEach((placement) => Object.entries(placement.facts || {}).forEach(([field, fact]) => facts.push({field, fact, placement: `${placement.portfolioName} / ${placement.account}`})));
    return facts.length ? (
        <div className="timeline">
            {facts.map(({field, fact, placement}, index) => (
                <div className="timeline-item" key={`${field}-${placement}-${index}`}>
                    <div>
                        <strong>{field}</strong>
                        <div className="subtle">{placement}</div>
                    </div>
                    <div>
                        <Badge value={fact.freshness}/>
                        {" "}
                        <strong>{fact.source}</strong>
                        <div className="subtle">
                            {fact.record || ""}
                            {fact.revision ? ` · revision ${fact.revision}` : ""}
                            {" "}
                            · observed
                            {" "}{formatDateTime(fact.observedAt)}
                        </div>
                        {fact.conflictNote ? <div className="warning">{fact.conflictNote}</div> : null}
                    </div>
                </div>
            ))}
        </div>
    ) : <div className="empty">No source facts linked.</div>;
}

function Timeline({strategy}) {
    return strategy.timeline?.length ? (
        <div className="timeline">
            {strategy.timeline.map((item, index) => (
                <div className="timeline-item" key={`${item.at}-${index}`}>
                    <div className="subtle mono">{formatDateTime(item.at)}</div>
                    <div>
                        <strong>{item.summary}</strong>
                        <div className="subtle">
                            {item.source}
                            {item.revision ? ` · ${item.revision}` : ""}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    ) : <div className="empty">No timeline events linked.</div>;
}

export function StrategyDetail({strategyRef, analysisWindow, refreshKey, onBack, onRefreshed}) {
    const [state, setState] = useState({loading: true, error: null, data: null});
    useEffect(() => {
        let cancelled = false;
        setState((current) => current.data?.strategyRef === strategyRef
            ? {...current, loading: false, error: null}
            : {loading: true, error: null, data: null});
        getJSON(`/api/strategies/${encodeURIComponent(strategyRef)}?window=${encodeURIComponent(analysisWindow)}`)
            .then((data) => {
                if (!cancelled) {
                    setState({loading: false, error: null, data});
                    onRefreshed(`Snapshot refreshed · ${data.window || analysisWindow} analysis · ${data.placements.length} placements`);
                }
            })
            .catch((error) => {
                if (!cancelled) setState({loading: false, error, data: null});
            });
        return () => {
            cancelled = true;
        };
    }, [strategyRef, analysisWindow, refreshKey, onRefreshed]);
    if (state.loading) return <LoadingState/>;
    if (state.error) return <ErrorState error={state.error}/>;
    const strategy = state.data;
    return (
        <>
            <section className="section">
                <div className="detail-header">
                    <div>
                        <div className="detail-title">
                            <h2>{strategy.strategyRef}</h2>
                            <Badge value={strategy.comparison}/>
                        </div>
                        <p className="detail-copy">{strategy.description}</p>
                        <div className="subtle mono">
                            {strategy.owner}
                            {" "}
                            ·
                            {" "}
                            {strategy.category}
                            {" "}
                            ·
                            {" "}
                            {strategy.side}
                        </div>
                    </div>
                    <div className="action-row"><button className="button secondary" onClick={onBack}>← Strategies</button></div>
                </div>
                <div className="stat-grid">
                    <Stat label={`${String(strategy.window || analysisWindow).toUpperCase()} P&L`} value={signed(strategy.netPnl)} className={metricClass(strategy.netPnl)}/>
                    <Stat label="Current risk" value={money(strategy.totalRiskUsd)}/>
                    <Stat label="Running in" value={`${strategy.placements.length} ${strategy.placements.length === 1 ? "portfolio" : "portfolios"}`}/>
                </div>
                {strategy.warnings?.length ? <div className="notice">{strategy.warnings.join(" ")}</div> : null}
            </section>
            <section className="section"><PnlCharts dailyPnl={strategy.dailyPnl}/></section>
            <TradesTable trades={strategy.trades} showStrategy={false} subtitle={`${strategy.window || analysisWindow} · closed and open trades`}/>
            <section className="section panel">
                <div className="panel-head section-head">
                    <div>
                        <h2>
                            Where
                            {" "}{strategy.strategyRef}
                            {" "}
                            is running
                        </h2>
                    </div>
                    <div className="subtle">
                        {strategy.placements.length}
                        {" "}
                        {strategy.placements.length === 1 ? "placement" : "placements"}
                    </div>
                </div>
                <PlacementTable strategy={strategy}/>
            </section>
            <details className="section panel detail-drawer">
                <summary className="panel-head section-head">
                    <div>
                        <h2>More details</h2>
                        <div className="subtle">Implementation, evidence, risk history, and sources</div>
                    </div>
                </summary>
                <div className="detail-drawer-grid">
                    <Identity strategy={strategy}/>
                    <section className="detail-drawer-section">
                        <h3>Risk history</h3>
                        <RiskHistory strategy={strategy}/>
                    </section>
                    <section className="detail-drawer-section">
                        <h3>Implementation</h3>
                        <dl className="info-list">
                            <div className="info-item">
                                <dt>Owner</dt>
                                <dd>{strategy.owner}</dd>
                            </div>
                            <div className="info-item">
                                <dt>Lifecycle</dt>
                                <dd>{strategy.lifecycle}</dd>
                            </div>
                            <div className="info-item">
                                <dt>Source</dt>
                                <dd>
                                    {strategy.sourceRepo}
                                    <br/>
                                    <span className="subtle">{strategy.sourceClass}</span>
                                </dd>
                            </div>
                            <div className="info-item">
                                <dt>Version</dt>
                                <dd>
                                    {strategy.currentVersion?.version || "—"}
                                    {" "}
                                    ·
                                    {" "}
                                    <span className="mono">{strategy.currentVersion?.codeCommit || "—"}</span>
                                </dd>
                            </div>
                            <div className="info-item">
                                <dt>Thesis</dt>
                                <dd>{strategy.thesis || "—"}</dd>
                            </div>
                        </dl>
                    </section>
                    <section className="detail-drawer-section">
                        <h3>Backtests and research</h3>
                        <Evidence strategy={strategy}/>
                    </section>
                    <RevisionHistory revisions={strategy.revisions} usage={strategy.revisionUsage}/>
                    <section className="detail-drawer-section">
                        <h3>Data sources</h3>
                        <Facts strategy={strategy}/>
                    </section>
                    <section className="detail-drawer-section">
                        <h3>Timeline</h3>
                        <Timeline strategy={strategy}/>
                    </section>
                </div>
            </details>
            <div className="footnote">
                Last snapshot:
                {" "}{formatDateTime(strategy.asOf)}
            </div>
        </>
    );
}
